<template>
  <div class="max-w-8xl mx-auto p-6">
    <h1 class="text-3xl font-bold text-gray-800 mb-8">
      TaxJar API orders status
    </h1>

    <div>
      <!-- Status Tabs -->
      <ul class="flex space-x-4 border-b mb-6">
        <li
          v-for="status in statusOptions"
          :key="status.value"
          class="cursor-pointer"
        >
          <button
            @click="statusSelected = status.value"
            :class="{
              'border-b-2 border-blue-500 text-blue-600':
                statusSelected === status.value,
              'text-gray-600 hover:text-gray-800':
                statusSelected !== status.value
            }"
            class="px-4 py-2 font-medium"
          >
            {{ status.label }}
          </button>
        </li>
      </ul>

      <!-- Search Bar -->
      <div class="mb-6">
        <div class="flex space-x-4">
          <input
            v-model="searchQuery"
            @keyup.enter="onSearch"
            type="text"
            placeholder="Search orders..."
            class="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            @click="onSearch"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
          >
            Search
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center py-8">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"
        ></div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
      >
        <strong>Error:</strong> {{ error }}
      </div>

      <!-- Orders Table -->
      <div
        v-else-if="orders && orders.length > 0"
        class="bg-white shadow-md rounded-lg overflow-hidden"
      >
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Order Number
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                City
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                State
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Postal Code
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                tax
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Amount
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Created Date
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Sync Date
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template v-for="(order, index) in orders">
              <!-- Main Order Row -->
              <tr
                :key="order.orderNumber"
                :class="{ 'bg-gray-50': index % 2 === 1 }"
              >
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                >
                  {{ order.orderNumber }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="{
                      'bg-red-100 text-red-800': order.taxjarStatus === 'error',
                      'bg-green-100 text-green-800':
                        order.taxjarStatus === 'success',
                      'bg-yellow-100 text-yellow-800':
                        order.taxjarStatus === 'pending',
                      'bg-gray-100 text-gray-800': ![
                        'error',
                        'success',
                        'pending'
                      ].includes(order.taxjarStatus)
                    }"
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  >
                    {{ order.taxjarStatus }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ order.city }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ order.state }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ order.postalCode }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ order.tax }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ order.total }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(order.createDate) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{
                    order.taxjarSyncDate
                      ? formatDate(order.taxjarSyncDate)
                      : "Not synced"
                  }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    @click="toggleLogs(order.orderNumber)"
                    :disabled="loadingLogs"
                    class="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                  >
                    <span v-if="loadingLogs">Loading...</span>
                    <span v-else-if="selectedOrderNumber === order.orderNumber"
                      >Hide Logs</span
                    >
                    <span v-else>View More</span>
                  </button>
                  <button
                    @click="sendOrderToTaxjar(order.orderNumber)"
                    class="ml-4 text-green-600 hover:text-green-900"
                  >
                    {{
                      sendingOrder === order.orderNumber
                        ? "Sending..."
                        : order.taxjarStatus === "success"
                        ? "Update Taxjar"
                        : "Send to Taxjar"
                    }}
                  </button>
                </td>
              </tr>

              <!-- Expanded Logs Row -->
              <tr
                v-if="selectedOrderNumber === order.orderNumber"
                :key="order.orderNumber + '-logs'"
              >
                <td colspan="10" class="px-6 py-4 bg-gray-50">
                  <div class="max-w-full">
                    <button
                      v-if="order.taxjarStatus === 'success'"
                      @click="refundTaxjarEntry(order.orderNumber)"
                      class="mb-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:ring-2 focus:ring-red-500"
                    >
                      Create Refund
                    </button>
                    <h4 class="text-lg font-medium text-gray-900 mb-3">
                      Logs for Order {{ order.orderNumber }}
                    </h4>

                    <!-- Logs Loading -->
                    <div v-if="loadingLogs" class="flex justify-center py-4">
                      <div
                        class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"
                      ></div>
                    </div>

                    <!-- Logs Error -->
                    <div
                      v-else-if="logsError"
                      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
                    >
                      Error loading logs: {{ logsError }}
                    </div>

                    <!-- Logs Table -->
                    <div
                      v-else-if="
                        currentOrderLogs && currentOrderLogs.length > 0
                      "
                      class="bg-white shadow rounded-lg overflow-hidden"
                    >
                      <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-100">
                          <tr>
                            <th
                              class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              ID
                            </th>
                            <th
                              class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              Status
                            </th>
                            <th
                              class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              Response
                            </th>
                            <th
                              class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              Created At
                            </th>
                          </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200">
                          <tr
                            v-for="log in currentOrderLogs"
                            :key="log.id"
                            class="hover:bg-gray-50"
                          >
                            <td class="px-4 py-2 text-sm text-gray-900">
                              {{ log.id }}
                            </td>
                            <td class="px-4 py-2">
                              <span
                                :class="{
                                  'bg-red-100 text-red-800':
                                    log.status === 'error',
                                  'bg-green-100 text-green-800':
                                    log.status === 'success',
                                  'bg-yellow-100 text-yellow-800':
                                    log.status === 'pending',
                                  'bg-gray-100 text-gray-800': ![
                                    'error',
                                    'success',
                                    'pending'
                                  ].includes(log.status)
                                }"
                                class="px-2 py-1 text-xs font-semibold rounded-full"
                              >
                                {{ log.status }}
                              </span>
                            </td>
                            <td
                              class="px-4 py-2 text-sm text-gray-600 max-w-xs"
                            >
                              <div class="truncate" :title="log.response">
                                {{ log.response }}
                              </div>
                            </td>
                            <td class="px-4 py-2 text-sm text-gray-500">
                              {{ formatDate(log.createdAt) }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- No Logs -->
                    <div v-else class="text-gray-500 py-4">
                      No logs found for this order.
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div
        v-if="pagination && pagination.totalPages > 1"
        class="mt-6 flex items-center justify-between"
      >
        <div class="text-sm text-gray-700">
          Showing page {{ pagination.currentPage }} of
          {{ pagination.totalPages }} ({{ pagination.totalRecords }} total
          records)
        </div>
        <div class="flex space-x-2">
          <button
            @click="changePage(pagination.currentPage - 1)"
            :disabled="!pagination.hasPrev"
            class="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="changePage(page)"
            :class="{
              'bg-blue-600 text-white': page === pagination.currentPage,
              'bg-white text-gray-700 hover:bg-gray-50':
                page !== pagination.currentPage
            }"
            class="px-3 py-2 text-sm border border-gray-300 rounded-md"
          >
            {{ page }}
          </button>
          <button
            @click="changePage(pagination.currentPage + 1)"
            :disabled="!pagination.hasNext"
            class="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      <!-- No Orders -->
      <div
        v-else-if="!loading && !orders"
        class="text-center py-8 text-gray-500"
      >
        No orders found for status "{{ statusSelected }}".
      </div>
    </div>

    <!-- Refund Modal -->
    <div
      v-if="showRefundModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="closeRefundModal"
    >
      <div
        class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white"
        @click.stop
      >
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            Create Refund for Order {{ refundOrderNumber }}
          </h3>
          <div class="mb-4">
            <label
              for="refundAmount"
              class="block text-sm font-medium text-gray-700 mb-2"
            >
              Refund Amount ($)
            </label>
            <input
              id="refundAmount"
              v-model="refundAmount"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              :disabled="refundLoading"
            />
          </div>
          <div class="flex justify-end space-x-3">
            <button
              @click="closeRefundModal"
              :disabled="refundLoading"
              class="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              @click="processRefund"
              :disabled="
                refundLoading || !refundAmount || parseFloat(refundAmount) <= 0
              "
              class="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              <span v-if="refundLoading">Processing...</span>
              <span v-else>Create Refund</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getSelectedStoreURL } from "@/api/httpStore.js";
import {
  getOrdersByStatusTaxjar,
  getOrderLogs,
  sendSingleOrder,
  refundTaxjarOrder
} from "@/api/store/Taxjar.js";

export default {
  name: "TaxjarOrders",
  data() {
    return {
      statusSelected: "error",
      orders: null,
      pagination: null,
      loading: false,
      error: null,
      selectedOrderNumber: null, // Only one order can be expanded at a time
      currentOrderLogs: [],
      loadingLogs: false,
      logsError: null,
      currentPage: 1,
      searchQuery: "",
      searchTimeout: null,
      showRefundModal: false,
      refundOrderNumber: null,
      refundAmount: "",
      refundLoading: false,
      statusOptions: [
        { value: "error", label: "Error" },
        { value: "success", label: "Success" },
        { value: "pending", label: "Pending" },
        { value: "all", label: "All" }
      ],
      sendingOrder: false
    };
  },
  computed: {
    baseUrl() {
      return getSelectedStoreURL();
    },
    visiblePages() {
      if (!this.pagination) return [];

      const current = this.pagination.currentPage;
      const total = this.pagination.totalPages;
      const pages = [];

      // Show max 5 pages around current page
      const start = Math.max(1, current - 2);
      const end = Math.min(total, current + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },
  mounted() {
    this.getTaxjarOrders();
  },
  watch: {
    statusSelected() {
      this.currentPage = 1;
      this.getTaxjarOrders();
    },
    searchQuery() {
      // Debounce search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.getTaxjarOrders();
      }, 500);
    }
  },
  methods: {
    async getTaxjarOrders() {
      this.loading = true;
      this.error = null;
      try {
        const response = await getOrdersByStatusTaxjar(
          this.statusSelected,
          this.currentPage,
          this.searchQuery
        );
        console.log("API Response:", response);
        this.orders = response.orders;
        this.pagination = response.pagination;
        console.log("Orders:", this.orders);
        console.log("Pagination:", this.pagination);
      } catch (error) {
        this.error = error.message || "Failed to load orders";
        console.error("Error fetching TaxJar orders:", error);
      } finally {
        this.loading = false;
      }
    },
    refundTaxjarEntry(orderNumber) {
      if (!orderNumber) return;

      this.refundOrderNumber = orderNumber;
      this.refundAmount = "";
      this.showRefundModal = true;
    },

    closeRefundModal() {
      this.showRefundModal = false;
      this.refundOrderNumber = null;
      this.refundAmount = "";
      this.refundLoading = false;
    },

    async processRefund() {
      if (!this.refundOrderNumber || !this.refundAmount) return;

      const amount = parseFloat(this.refundAmount);

      // Validate amount
      if (isNaN(amount) || amount <= 0) {
        this.$toast.error("Please enter a valid refund amount greater than 0.");
        return;
      }

      this.refundLoading = true;

      try {
        await refundTaxjarOrder(this.refundOrderNumber, amount);
        this.$toast.success(
          `TaxJar order ${this.refundOrderNumber} refunded $${amount.toFixed(
            2
          )} successfully.`
        );

        // Close modal
        this.closeRefundModal();

        // Refresh orders list to reflect changes
        this.getTaxjarOrders();

        // Refresh logs if this order is currently expanded
        if (this.selectedOrderNumber === this.refundOrderNumber) {
          await this.loadOrderLogs(this.refundOrderNumber);
        }
      } catch (error) {
        this.$toast.error(
          `Failed to refund TaxJar order ${
            this.refundOrderNumber
          }: ${error.message || "Unknown error"}`
        );
        console.error(
          `Error refunding TaxJar order ${this.refundOrderNumber}:`,
          error
        );
      } finally {
        this.refundLoading = false;
      }
    },
    async sendOrderToTaxjar(orderNumber) {
      if (this.sendingOrder) return; // Prevent multiple clicks
      if (!orderNumber) return;

      this.sendingOrder = orderNumber;

      try {
        await sendSingleOrder(orderNumber);
        this.$toast.success(
          `Order ${orderNumber} sent to TaxJar successfully.`
        );
        // Optionally refresh the orders list to reflect any status changes
        this.getTaxjarOrders();
      } catch (error) {
        this.$toast.error(
          `Failed to send order ${orderNumber} to TaxJar: ${error.message ||
            "Unknown error"}`
        );
        console.error(`Error sending order ${orderNumber} to TaxJar:`, error);
      } finally {
        this.sendingOrder = false;
      }
    },
    async toggleLogs(orderNumber) {
      // If already expanded, collapse
      if (this.selectedOrderNumber === orderNumber) {
        this.selectedOrderNumber = null;
        this.currentOrderLogs = [];
        this.logsError = null;
        return;
      }

      // Expand and load logs
      this.selectedOrderNumber = orderNumber;
      this.currentOrderLogs = [];
      this.logsError = null;
      await this.loadOrderLogs(orderNumber);
    },

    async loadOrderLogs(orderNumber) {
      this.loadingLogs = true;
      this.logsError = null;

      try {
        const logs = await getOrderLogs(orderNumber);
        this.currentOrderLogs = logs;
      } catch (error) {
        this.logsError = error.message || "Failed to load logs";
        console.error(`Error fetching logs for order ${orderNumber}:`, error);
      } finally {
        this.loadingLogs = false;
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.currentPage = page;
        this.getTaxjarOrders();
      }
    },

    onSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.currentPage = 1;
      this.getTaxjarOrders();
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        return new Date(dateString).toLocaleString();
      } catch (error) {
        return dateString;
      }
    }
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
};
</script>

<style scoped>
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
